import * as yup from 'yup';
import {
  AddIpnData,
  Ipn,
  IpnValidations,
  UpdateIpnData,
} from 'types/ipn.types';
import { fieldValidationMessages } from 'app/constants';
import { emptyStringToNull, testIpn } from 'app/utils/utils';
import { SocietyOption } from '../types';
import { IpnForm } from './types';
import { affiliationToSocietyOption } from '../utils';
import {
  manualRequiredIdNameValidation,
  oneOfRequiredIdNameValidation,
} from '../validations';

const ipnNumberValidation = (ipnMin: number, ipnMax: number) =>
  yup.string().when('society', {
    is: (option?: SocietyOption) => !option?.verification,
    then: yup
      .string()
      .required(fieldValidationMessages.REQUIRED)
      .test('validate-ipn', 'Should be 8 digits', testIpn(ipnMin, ipnMax)),
    otherwise: yup.string().nullable(),
  });

export const toAddIpnData = ({
  society,
  ipnNumber,
  ...rest
}: Partial<IpnForm>): AddIpnData => ({
  cmoSocietyAffiliation: society?.id || '',
  ipnNumber: Number(ipnNumber),
  ...rest,
});

export const toUpdateIpnData = ({
  firstName,
  lastName,
}: Partial<IpnForm>): Partial<UpdateIpnData> => ({
  firstName: emptyStringToNull(firstName),
  lastName: emptyStringToNull(lastName),
});

export const getDefaultValues = (ipn?: Ipn) => ({
  society: ipn?.cmoSocietyAffiliation
    ? affiliationToSocietyOption(ipn.cmoSocietyAffiliation)
    : null,
  ipnNumber: ipn?.ipnNumber.toString() || '',
  firstName: ipn?.firstName || '',
  lastName: ipn?.lastName || '',
  isDefault: ipn?.default ?? false,
});

const getDefaultAddIpnValidationSchema = () => {
  const defaultMaxLength = 255;
  const defaultIpnMin = 0;
  const defaultIpnMax = 999999999;
  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      ipnNumber: ipnNumberValidation(defaultIpnMin, defaultIpnMax),
      firstName: manualRequiredIdNameValidation(defaultMaxLength),
      lastName: manualRequiredIdNameValidation(defaultMaxLength),
    },
    [['firstName', 'lastName']]
  );
};

export const getAddIpnValidationSchema = (validations?: IpnValidations) => {
  if (!validations) {
    return getDefaultAddIpnValidationSchema();
  }
  const anyOf = validations.anyOf.map((el) => el.required).flat();
  const { firstName, lastName, ipnNumber } = validations.properties;
  const firstNameMax = firstName.maxLength;
  const lastNameMax = lastName.maxLength;
  const ipnMin = ipnNumber.minimum;
  const ipnMax = ipnNumber.maximum;

  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      ipnNumber: ipnNumberValidation(ipnMin, ipnMax),
      ...(anyOf.includes('firstName') && anyOf.includes('lastName')
        ? {
            firstName: oneOfRequiredIdNameValidation('lastName', firstNameMax),
            lastName: oneOfRequiredIdNameValidation('firstName', lastNameMax),
          }
        : {
            firstName: manualRequiredIdNameValidation(firstNameMax),
            lastName: manualRequiredIdNameValidation(lastNameMax),
          }),
    },
    [['firstName', 'lastName']]
  );
};

export const getUpdateIpnValidationSchema = (validations?: IpnValidations) => {
  const { firstName, lastName } = validations?.properties || {};
  const defaultMaxLength = 255;
  const firstNameMax = firstName?.maxLength || defaultMaxLength;
  const lastNameMax = lastName?.maxLength || defaultMaxLength;

  return yup.object().shape(
    {
      society: yup.object().typeError(fieldValidationMessages.REQUIRED),
      firstName: oneOfRequiredIdNameValidation('lastName', firstNameMax),
      lastName: oneOfRequiredIdNameValidation('firstName', lastNameMax),
    },
    [['firstName', 'lastName']]
  );
};
